import {Component, inject, Input} from '@angular/core';
import {ColDef, GridApi, GridReadyEvent} from "ag-grid-community";
import {AgGridAngular} from "ag-grid-angular";
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {MatCard, MatCardContent, MatCardHeader, MatCardTitle} from "@angular/material/card";
import {MatError, MatFormField, MatLabel} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {MatButton} from "@angular/material/button";
import {IGameQueryResult} from "../../interfaces/IGameQueryResult";
import {ICausableBalance} from "../../interfaces/causable/ICausableBalance";
import {ICausablePayment} from "../../interfaces/causable/ICausablePayment";
import {CausablePaymentService} from "../../services/causable-payment.service";
import {ErrorHandlingService} from "../../services/error-handling.service";
import {SnackbarService} from "../../services/snackbar.service";
import {UtilService} from "../../services/util.service";
import {FormatCurrencyPipe} from "../../pipes/format-currency.pipe";
import {PipesModule} from "../../pipes/pipes.module";
import {LoadingCardContentComponent} from "../../shared/loading-card-content/loading-card-content.component";
import {
  TableExportButtonsComponent
} from "../../components/table-export-buttons/table-export-buttons.component";
import {ITableExportRequest, TableExportService} from "../../services/table-export.service";
import {DatePipe} from "@angular/common";
import {UIStateEnum} from "../../enum/UIStateEnum";
import {MatDialog} from "@angular/material/dialog";
import {DialogFunctionService} from "../../services/dialog-function.service";
import {
  ConfirmationActionDialogComponent
} from "../../components/dialogs/confirmation-action-dialog/confirmation-action-dialog.component";
import {IDialogGenericData} from "../../interfaces/IDialogGenericData";
import {VoidRemittanceActionComponent} from "./void-remittance-action.component";

@Component({
  selector: 'app-causable-ledger',
  standalone: true,
  imports: [
    AgGridAngular,
    ReactiveFormsModule,
    MatCard,
    MatCardHeader,
    MatCardTitle,
    MatCardContent,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    MatButton,
    PipesModule,
    LoadingCardContentComponent,
    TableExportButtonsComponent
  ],
  templateUrl: './causable-ledger.component.html',
  styleUrls: ['./causable-ledger.component.scss']
})
export class CausableLedgerComponent {

  private causablePaymentService: CausablePaymentService = inject(CausablePaymentService);
  private errorHandlingService: ErrorHandlingService = inject(ErrorHandlingService);
  private snackbarService: SnackbarService = inject(SnackbarService);
  private utilService: UtilService = inject(UtilService);
  private datePipe: DatePipe = inject(DatePipe);
  private currencyPipe: FormatCurrencyPipe = inject(FormatCurrencyPipe);
  private matDialog: MatDialog = inject(MatDialog);
  private dialogFunctionService: DialogFunctionService = inject(DialogFunctionService);
  private tableExportService: TableExportService = inject(TableExportService);

  @Input() set gameData(gameP: IGameQueryResult) {
    this.activeGame = gameP;
    this.resetForm();
    this.refresh();
  }

  public activeGame: IGameQueryResult | null = null;
  public balance: ICausableBalance | null = null;
  public payments: ICausablePayment[] = [];
  public isSubmitting = false;
  public uiState = UIStateEnum.ShowData;
  protected readonly UIStateEnum = UIStateEnum;

  private paymentsGridApi!: GridApi<ICausablePayment>;

  public remittanceForm = new FormGroup({
    amount: new FormControl<number | null>(null, [Validators.required, Validators.min(0.01)]),
    paymentDate: new FormControl<string | null>(CausableLedgerComponent.todayIso()),
    referenceNumber: new FormControl<string | null>(null)
  });

  /**
   * Today as yyyy-MM-dd in the admin's *local* calendar — a remittance defaults to "received today".
   * Not `toISOString()`: that converts to UTC, so an admin west of UTC entering a remittance in the
   * evening would get tomorrow's date pre-filled. Matches UtilService.formatCalendarDate, which reads
   * local components for the same reason.
   */
  private static todayIso(): string {
    const now = new Date();
    const month = `${now.getMonth() + 1}`.padStart(2, '0');
    const day = `${now.getDate()}`.padStart(2, '0');

    return `${now.getFullYear()}-${month}-${day}`;
  }

  /**
   * The receivable ledger as shown, for CSV and PDF (#1411). Money exports as a plain number and dates as ISO, so
   * the amount column can be totalled and the dates sort — this is a file someone reconciles against a bank.
   */
  private buildExportRequest(): ITableExportRequest<ICausablePayment> {
    return {
      title: 'Causable payments received',
      subtitle: this.activeGame?.Name ?? '',
      summary: this.exportSummary(),
      rows: this.exportRows(),
      columns: [
        {
          header: 'Payment date',
          value: paymentP => this.utilService.formatCalendarDate(paymentP.PaymentDate),
          csvValue: paymentP => this.tableExportService.csvDate(paymentP.PaymentDate)
        },
        {
          header: 'Amount',
          value: paymentP => this.currencyPipe.transform(paymentP.Amount),
          csvValue: paymentP => this.tableExportService.csvMoney(paymentP.Amount),
          numeric: true
        },
        {header: 'Reference', value: paymentP => paymentP.ReferenceNumber || '—'},
        {
          header: 'Recorded On',
          // The grid prints a date, so the PDF prints a date — an export that quietly says more than the screen is still
          // an export that disagrees with it.
          value: paymentP => paymentP.CreatedOnDate
            ? this.datePipe.transform(paymentP.CreatedOnDate, 'MMM d, y') ?? '—'
            : '—',
          csvValue: paymentP => this.tableExportService.csvDateTime(paymentP.CreatedOnDate)
        },
        {header: 'Status', value: paymentP => paymentP.Voided ? 'Voided' : 'Applied'}
      ],
      fileName: this.tableExportService.fileNameFor([
        this.activeGame?.ShortName || this.activeGame?.Name,
        'causable-payments'
      ])
    };
  }

  /**
   * The remittances in the order the grid shows them — Payment Date descending, the grid's declared default
   * sort. `payments` arrives ordered by when each row was *entered* (created_on_date), which only matches the
   * screen when remittances happen to be recorded in payment-date order. Record a cheque dated Aug 10 and then
   * one dated Aug 1 and the two disagree, on the one file in this app that gets reconciled against a bank.
   *
   * This still ignores whatever the user has since sorted or filtered in the grid; the export request takes
   * plain rows. `gridApi.forEachNodeAfterFilterAndSort` is the source that cannot drift, when that is worth
   * changing the shared helper for.
   */
  private exportRows(): ICausablePayment[] {
    return [...this.payments].sort((firstP, secondP) =>
      (secondP.PaymentDate ?? '').localeCompare(firstP.PaymentDate ?? ''));
  }

  /**
   * The three figures the screen leads with, not a row count. This is a file someone reconciles against a
   * bank statement: what was earned, what came in, and what is still outstanding are the reason to export it.
   *
   * A game that settles its fees directly has no receivable at all — the screen says so above the table, and
   * a file that omits that reads as an organization which has remitted nothing.
   */
  private exportSummary(): {label: string, value: string}[] {
    if (!this.balance) {
      return [{label: 'Payments', value: `${this.payments.length}`}];
    }

    const summary = [
      {label: 'Fees earned', value: this.currencyPipe.transform(this.balance.TotalCausableFeesEarned)},
      {label: 'Remitted', value: this.currencyPipe.transform(this.balance.TotalPaid)}
    ];

    if (this.settlesByInvoice) {
      summary.push({label: this.owedLabel, value: this.currencyPipe.transform(this.owedDisplayAmount)});
    } else {
      summary.push({
        label: 'Receivable',
        value: "None — Causable collects this game's fees directly at the time of payment"
      });
    }

    summary.push({label: 'Payments', value: `${this.payments.length}`});

    return summary;
  }

  onExportCsv(): void {
    this.tableExportService.exportCsv(this.buildExportRequest());
  }

  onExportPdf(): void {
    this.tableExportService.exportPdf(this.buildExportRequest());
  }

  public paymentColDefs: ColDef[] = [
    {
      headerName: 'Payment Date',
      field: 'PaymentDate',
      resizable: true,
      sortable: true,
      sort: 'desc',
      flex: 1,
      // payment_date is a calendar date, not an instant — see UtilService.formatCalendarDate.
      valueFormatter: params => this.utilService.formatCalendarDate(params.value)
    },
    {
      headerName: 'Amount',
      field: 'Amount',
      resizable: true,
      sortable: true,
      flex: 1,
      valueFormatter: params => this.currencyPipe.transform(params.value)
    },
    {
      headerName: 'Reference',
      field: 'ReferenceNumber',
      resizable: true,
      sortable: true,
      flex: 1,
      valueFormatter: params => params.value || '—'
    },
    {
      headerName: 'Recorded On',
      field: 'CreatedOnDate',
      resizable: true,
      sortable: true,
      flex: 1,
      valueFormatter: params => params.value ? new Date(params.value).toLocaleDateString() : '—'
    },
    {
      headerName: 'Status',
      colId: 'Status',
      resizable: true,
      sortable: true,
      flex: 1,
      // A voided remittance stays on the ledger for audit but is excluded from the balance, so it has to be visibly
      // distinct rather than silently dropped.
      valueGetter: params => params.data?.Voided ? 'Voided' : 'Applied',
      cellStyle: params => params.data?.Voided ? {color: '#b71c1c', fontWeight: 'bold'} : null
    },
    {
      headerName: 'Actions',
      colId: 'Actions',
      resizable: true,
      flex: 1,
      cellRenderer: VoidRemittanceActionComponent,
      cellRendererParams: {
        onVoidClick: (causablePaymentIdP: string) => this.onVoidClick(causablePaymentIdP)
      }
    }
  ];

  /**
   * Whether this game actually owes Causable anything. False for games where Causable collects the fee
   * at charge time — the fee is already ours, so there is no receivable and nothing to remit.
   */
  get settlesByInvoice(): boolean {
    return this.balance?.SettlesCausableFeesByInvoice === true;
  }

  /** Owed is negative when the org has over-remitted — that is a credit, not a debt. */
  get isInCredit(): boolean {
    return !!this.balance && this.balance.AmountOwed < 0;
  }

  get owedLabel(): string {
    return this.isInCredit ? 'Credit Held' : 'Amount Owed to Causable';
  }

  get owedDisplayAmount(): number {
    return this.balance ? Math.abs(this.balance.AmountOwed) : 0;
  }

  refresh(): void {
    if (!this.activeGame) return;

    this.uiState = UIStateEnum.ShowLoading;

    this.causablePaymentService.fetchBalanceByGameId(this.activeGame.Id).subscribe({
      next: (balance) => {
        this.balance = balance;
        this.uiState = UIStateEnum.ShowData;
      },
      error: (err) => {
        this.uiState = UIStateEnum.ShowData;
        this.errorHandlingService.displayPageLevelErrorMessage(err);
      }
    });

    this.causablePaymentService.fetchPaymentsByGameId(this.activeGame.Id).subscribe({
      next: (payments) => {
        this.payments = payments;
      },
      error: (err) => {
        this.errorHandlingService.displayPageLevelErrorMessage(err);
      }
    });
  }

  sanitizeCurrencyInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const raw_value = input.value;
    const cleaned_value = this.utilService.sanitizeCurrencyString(raw_value);
    const numeric_value = this.utilService.parseCurrencyValue(raw_value);

    if (numeric_value !== null) {
      this.remittanceForm.patchValue({amount: numeric_value}, {emitEvent: false});
      input.value = cleaned_value;
    } else if (cleaned_value === '' || cleaned_value === '.') {
      this.remittanceForm.patchValue({amount: null}, {emitEvent: false});
      input.value = cleaned_value;
    } else {
      const current_value = this.remittanceForm.get('amount')?.value;
      input.value = current_value !== null && current_value !== undefined ? String(current_value) : '';
    }
  }

  fillFullAmountOwed(): void {
    if (this.balance && this.balance.AmountOwed > 0) {
      this.remittanceForm.patchValue({amount: this.utilService.centsToDollars(this.balance.AmountOwed)});
    }
  }

  onSubmit(): void {
    if (this.remittanceForm.invalid || this.isSubmitting || !this.activeGame) {
      return;
    }

    this.isSubmitting = true;
    const form_value = this.remittanceForm.value;

    this.causablePaymentService.recordCausablePayment({
      GameId: this.activeGame.Id,
      // The backend stores cents; the form collects dollars.
      Amount: Math.round((form_value.amount || 0) * 100),
      PaymentDate: form_value.paymentDate || null,
      ReferenceNumber: form_value.referenceNumber && form_value.referenceNumber.trim() ? form_value.referenceNumber.trim() : null
    }).subscribe({
      next: () => {
        this.snackbarService.openSuccessfulSnackBar('Remittance recorded successfully!');
        this.resetForm();
        this.isSubmitting = false;
        this.refresh();
      },
      error: (err) => {
        this.errorHandlingService.displayPageLevelErrorMessage(err);
        this.isSubmitting = false;
      }
    });
  }

  /**
   * Voiding reverses money on a financial ledger, so it is confirmed first and the amount is spelled
   * out in the prompt — the grid row is the only other place the admin could check what they clicked.
   */
  onVoidClick(causablePaymentIdP: string): void {
    if (!this.activeGame) return;

    const payment = this.payments.find(paymentP => paymentP.Id === causablePaymentIdP);
    const game_id = this.activeGame.Id;

    this.dialogFunctionService.setCallback(() =>
      this.causablePaymentService.voidCausablePayment(game_id, causablePaymentIdP));

    const dialog_data: IDialogGenericData = {
      title: 'Confirm Voiding Remittance',
      message: `Confirm that you wish to void the ${this.currencyPipe.transform(payment?.Amount ?? 0)} remittance`
        + `${payment?.ReferenceNumber ? ` (${payment.ReferenceNumber})` : ''}. `
        + `The amount will no longer count toward what this organization has paid.`,
      successMessage: 'Successfully voided the remittance',
      dataCyTag: 'confirm-void-remittance'
    };

    this.matDialog.open(ConfirmationActionDialogComponent, {data: dialog_data, width: '650px'})
      .afterClosed()
      .subscribe(() => this.refresh());
  }

  private resetForm(): void {
    this.remittanceForm.reset();
    this.remittanceForm.patchValue({paymentDate: CausableLedgerComponent.todayIso()});
  }

  onPaymentsGridReady(params: GridReadyEvent<ICausablePayment>) {
    this.paymentsGridApi = params.api;
    this.paymentsGridApi.sizeColumnsToFit({defaultMinWidth: 100});
  }
}
